

import React, { useState } from "react";
import { Trash2, ChevronRight, TrendingUp, Users, DollarSign } from "lucide-react";
import { useInstallEventsAggregation } from "@/hooks/useInstallEventsAggregation";
import CopyButton from "@/components/CopyButton";
import { RosterEntry } from "@/types";
import { getAuth } from "firebase/auth";

interface Props {
  entry: RosterEntry;
  gameName: string;
  gameSlug?: string;
  gameStatus?: 'available' | 'hidden' | 'archived';
  gameIcon?: string;
  gameplayVideos?: string[];
  isArchived: boolean;
  removing: boolean;
  onRemove: () => void;
  onClick: () => void;
  onRestore?: () => void | Promise<void>;
}

export function RosterCard({
  entry,
  gameName,
  gameSlug,
  gameStatus,
  gameIcon,
  isArchived,
  removing,
  onRemove,
  onClick,
  onRestore,
}: Props) {
  const auth = getAuth();
  const userId = auth.currentUser?.uid;
  const [restoring, setRestoring] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [archivingLocal, setArchivingLocal] = useState(false);

  const handleRestore = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!onRestore) return;
    try {
      setRestoring(true);
      await onRestore();
    } finally {
      setRestoring(false);
    }
  };

  // Use dynamic earnings aggregation for this specific game
  // Try to extract normalized ref from the affiliate link so we can scope
  // install event aggregation to this specific social account's affiliate link.
  let refForAggregation: string | undefined = undefined;
  try {
    const u = new URL(entry.affiliateLink);
    const r = u.searchParams.get('ref');
    if (r) refForAggregation = r;
  } catch (e) {
    const raw = String(entry.affiliateLink || '').split('?')[1] || '';
    const m = raw.match(/ref=([^&]+)/);
    if (m && m[1]) refForAggregation = decodeURIComponent(m[1]);
  }

  const { totalEarnings, totalInstalls, loading } = useInstallEventsAggregation(
    userId || "",
    "lifetime",
    gameSlug,
    refForAggregation
  );

  const averageCPI = totalInstalls > 0 ? totalEarnings / totalInstalls : 0;

  const getInitials = (name: string) => {
    if (!name) return "G";
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[1][0]).toUpperCase();
  };

  return (
    <div
      data-game-status={gameStatus}
      className={`group relative overflow-hidden rounded-3xl border transition-all duration-300 ${
        isArchived
          ? "border-white/5 bg-zinc-900/30 opacity-60"
          : "border-white/10 bg-gradient-to-br from-zinc-900/90 via-zinc-900/70 to-zinc-900/90 hover:border-lime-400/30 hover:shadow-lg hover:shadow-lime-400/5"
      } text-white`}
    >
      {/* If the campaign was marked archived at the game level, show an overlay */}
      {gameStatus === 'archived' && (
        <div className="absolute inset-0 z-30 flex items-center justify-center rounded-3xl bg-black/70 p-4 text-center">
          <div className="max-w-xs">
            <div className="mb-2 text-sm font-semibold text-white">This campaign is over</div>
            <div className="text-xs text-white/60">This campaign has finished and is no longer active.</div>
          </div>
        </div>
      )}
      {/* Gradient overlay on hover */}
      {!isArchived && (
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-lime-400/0 via-emerald-500/0 to-lime-400/0 opacity-0 transition-opacity duration-300 group-hover:opacity-5" />
      )}

      <div className="relative p-5">
        {/* Header - Game name and total earnings */}
        <div className="mb-4 flex items-start gap-3">
          <button
            onClick={onClick}
            className="flex-1 flex items-start gap-3 transition group-hover:translate-x-0.5 text-left"
          >
            <div className="flex-shrink-0">
              {gameIcon ? (
                <img
                  src={gameIcon}
                  alt={`${gameName} icon`}
                  className="h-16 w-16 rounded-xl object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-xl bg-white/5 text-base font-bold text-white/90">
                  {getInitials(gameName)}
                </div>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg sm:text-xl font-bold tracking-tight transition group-hover:text-lime-300">
                {gameName}
              </h3>
              <p className="mt-1 text-xs text-white/40">Lifetime performance</p>
            </div>
            <div className="flex items-center gap-2 text-sm font-medium text-white/60">
              <span className="hidden sm:inline">View more</span>
              <ChevronRight className="h-5 w-5 text-white/20 transition group-hover:translate-x-1 group-hover:text-lime-400" />
            </div>
          </button>

          {/* Video preview removed from roster card per design */}
        </div>

        {/* Stats Grid - show Total Earnings first, then Installs */}
        {!loading && (
          <div className="mb-4 grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-white/5 bg-black/30 p-3">
              <div className="flex items-center gap-2 text-white/50">
                <DollarSign className="h-3.5 w-3.5 text-lime-400" />
                <span className="text-xs font-medium">Total Earnings</span>
              </div>
              <p className="mt-1.5 text-lg font-bold">${totalEarnings.toFixed(2)}</p>
            </div>
            <div className="rounded-2xl border border-white/5 bg-black/30 p-3">
              <div className="flex items-center gap-2 text-white/50">
                <Users className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Installs</span>
              </div>
              <p className="mt-1.5 text-lg font-bold">{totalInstalls.toLocaleString()}</p>
            </div>
            <div className="rounded-2xl border border-white/5 bg-black/30 p-3">
              <div className="flex items-center gap-2 text-white/50">
                <TrendingUp className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Avg CPI</span>
              </div>
              <p className="mt-1.5 text-lg font-bold">${averageCPI.toFixed(3)}</p>
            </div>
          </div>
        )}

        {/* Affiliate Link */}
        <div className="mb-3">
          <p className="mb-2 text-xs font-medium text-white/50">Affiliate Link</p>
          <div className="flex items-center gap-2 rounded-2xl border border-white/10 bg-black/40 p-3 transition hover:border-white/20">
            <span className="flex-1 truncate text-xs text-white/70">
              {entry.affiliateLink}
            </span>
            <CopyButton text={entry.affiliateLink} />
          </div>
        </div>

        {/* Actions */}
        {!isArchived ? (
          <>
            <button
              onClick={(e) => {
                e.stopPropagation();
                setShowArchiveConfirm(true);
              }}
              disabled={removing}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-red-500/20 bg-red-500/10 px-3 py-2 text-xs font-medium text-red-300 transition hover:border-red-500/40 hover:bg-red-500/20 disabled:opacity-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {removing ? "Archiving..." : "Archive & free slot"}
            </button>

            {/* Archive confirmation modal */}
            {showArchiveConfirm && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setShowArchiveConfirm(false);
                }}
                className="absolute inset-0 z-20 flex items-center justify-center bg-black/60 p-4"
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="w-full max-w-md rounded-2xl border border-white/10 bg-zinc-900 p-6 text-white"
                >
                  <h4 className="text-sm font-semibold">Are you sure?</h4>
                  <p className="mt-2 text-xs text-white/60">
                    Are you sure you want to archive this game? You can recover it later, but archived games will not earn any
                    revenue while archived.
                  </p>

                  <div className="mt-4 flex items-center justify-end gap-2">
                    <button
                      onClick={() => setShowArchiveConfirm(false)}
                      className="rounded-lg px-3 py-2 text-xs font-medium text-white/70 hover:bg-white/5"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!onRemove) return;
                        try {
                          setArchivingLocal(true);
                          await onRemove();
                        } finally {
                          setArchivingLocal(false);
                          setShowArchiveConfirm(false);
                        }
                      }}
                      disabled={removing || archivingLocal}
                      className="rounded-lg bg-red-600 px-3 py-2 text-xs font-medium text-white disabled:opacity-50"
                    >
                      {removing || archivingLocal ? "Archiving..." : "Archive & free slot"}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-2">
            <div className="text-xs text-white/40 text-center">Archived</div>
            {gameStatus === 'archived' ? (
              <div className="text-xs text-center text-white/60">This campaign has ended and cannot be restored.</div>
            ) : (
              <button
                onClick={handleRestore}
                disabled={restoring}
                className="flex w-full items-center justify-center gap-2 rounded-xl border border-lime-400/20 bg-lime-400/10 px-3 py-2 text-xs font-medium text-lime-300 transition hover:bg-lime-400/20 disabled:opacity-50"
              >
                {restoring ? "Restoring..." : "Restore & occupy slot"}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
